import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import "./ReassignOwnerModal.css";

// Super-admin reassignment picker. Searches socialUsers by name (first /
// last / display / email), shows full records so the admin can verify
// identity, and surfaces the selected user's email to the caller.
//
// Reusable: pass props.recordLabel (e.g. "event", "image", "location")
// and props.currentOwner so the dialog can self-describe. The caller is
// responsible for actually firing the relevant graphql mutation when
// onSelect is invoked with an email (or empty string to clear).
const SEARCH_USERS = gql`
  query SearchSocialUsers($query: String!) {
    searchSocialUsers(query: $query) {
      userId
      email
      firstName
      lastName
      name
      phone
      sex
      shirtSize
      interestedEvents
      referredBy
      approved
      paid
      joinDate
      lastPaidDate
      stripeEmail
      groupMeUserId
      rawJson
    }
  }
`;

const SEARCH_DEBOUNCE_MS = 300;

const ReassignOwnerModal = ({
  open,
  onClose,
  onSelect, // (emailOrEmpty: string) => Promise<void> | void
  recordLabel = "record",
  recordTitle = "",
  currentOwner = "",
}) => {
  const [query, setQuery] = useState("");
  const [expandedRaw, setExpandedRaw] = useState({});
  const [search, { data, loading, error }] = useLazyQuery(SEARCH_USERS, {
    fetchPolicy: "network-only",
  });
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  // Reset / focus when the modal opens.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setExpandedRaw({});
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 50);
  }, [open]);

  // Debounce searches so a fast typist doesn't fire one query per keystroke.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) return;
    debounceRef.current = setTimeout(() => {
      search({ variables: { query: q } });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, search, open]);

  const results = data?.searchSocialUsers || [];

  const handlePick = useCallback(
    async (user) => {
      if (!user) return;
      const email = (user.email || "").trim().toLowerCase();
      if (!email) {
        if (!window.confirm(
          `${user.firstName || user.name || user.userId} has no email on record. ` +
            "Assigning will set an unusable owner that no captain can ever match. " +
            "Continue?"
        )) {
          return;
        }
      }
      await onSelect(email);
    },
    [onSelect]
  );

  const handleClear = useCallback(async () => {
    if (!window.confirm("Clear ownership? Only admins will be able to delete this record.")) {
      return;
    }
    await onSelect("");
  }, [onSelect]);

  const prettyRaw = useMemo(() => {
    return (uid) => {
      const row = results.find((r) => r.userId === uid);
      if (!row) return "";
      try {
        return JSON.stringify(JSON.parse(row.rawJson), null, 2);
      } catch {
        return row.rawJson;
      }
    };
  }, [results]);

  if (!open) return null;

  return (
    <div
      className="reassign-modal__backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="reassign-modal__panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="reassign-modal__header">
          <h3 className="reassign-modal__title">
            Reassign {recordLabel}
            {recordTitle ? ` — ${recordTitle}` : ""}
          </h3>
          <button
            type="button"
            className="reassign-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="reassign-modal__current">
          Current owner: <code>{currentOwner || "(no recorded owner)"}</code>
        </div>

        <div className="reassign-modal__search-row">
          <input
            ref={inputRef}
            type="search"
            className="reassign-modal__search"
            placeholder="Search by first or last name, email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            type="button"
            className="reassign-modal__btn"
            onClick={handleClear}
            title="Remove ownership (admin-only deletable after)"
          >
            Clear owner
          </button>
        </div>

        {query.trim().length > 0 && query.trim().length < 2 && (
          <div className="reassign-modal__hint">Type at least 2 characters…</div>
        )}

        {loading && <div className="reassign-modal__hint">Searching…</div>}
        {error && (
          <div className="reassign-modal__error">
            Search failed: {error.message}
          </div>
        )}

        {!loading && !error && query.trim().length >= 2 && results.length === 0 && (
          <div className="reassign-modal__hint">
            No matches for "{query.trim()}".
          </div>
        )}

        <div className="reassign-modal__results">
          {results.map((u) => {
            const displayName =
              [u.firstName, u.lastName].filter(Boolean).join(" ") ||
              u.name ||
              u.email ||
              u.userId;
            const isExpanded = !!expandedRaw[u.userId];
            return (
              <div key={u.userId} className="reassign-modal__row">
                <div className="reassign-modal__row-head">
                  <div className="reassign-modal__row-main">
                    <div className="reassign-modal__row-name">
                      {displayName}
                    </div>
                    <div className="reassign-modal__row-meta">
                      <span>{u.email || "(no email)"}</span>
                      {u.phone && <span>· {u.phone}</span>}
                      {u.sex && <span>· {u.sex}</span>}
                      {typeof u.paid === "boolean" && (
                        <span>· {u.paid ? "paid" : "unpaid"}</span>
                      )}
                      {typeof u.approved === "boolean" && (
                        <span>
                          · {u.approved ? "approved" : "not approved"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="reassign-modal__row-actions">
                    <button
                      type="button"
                      className="reassign-modal__btn"
                      onClick={() =>
                        setExpandedRaw((s) => ({
                          ...s,
                          [u.userId]: !s[u.userId],
                        }))
                      }
                    >
                      {isExpanded ? "Hide record" : "Full record"}
                    </button>
                    <button
                      type="button"
                      className="reassign-modal__btn reassign-modal__btn--primary"
                      onClick={() => handlePick(u)}
                      disabled={!u.email}
                      title={
                        u.email
                          ? `Assign ownership to ${u.email}`
                          : "No email on record"
                      }
                    >
                      Pick
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <pre className="reassign-modal__raw">{prettyRaw(u.userId)}</pre>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ReassignOwnerModal;
